import React from "react";
import PropTypes from "prop-types";
import { Link, withRouter } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { ThemeProvider, withStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Drawer from "@material-ui/core/Drawer";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import FilterListIcon from "@material-ui/icons/FilterList";

import TirePairCard from "../list/TirePairCard";
import TireRequestCard from "../list/TireRequestCard";
import { formatIls, getPairKey } from "../list/tirePairModel";
import TirePairFiltersPanel from "./TirePairFiltersPanel";
import {
  DEFAULT_PAIR_FILTERS,
  applyPairFilters,
  countActivePairFilters,
  getPairFacets,
  getSearchForPairFilters,
  getVisiblePairFilterSections,
  parsePairFiltersFromSearch,
} from "./tirePairFilters";
import {
  getMotoTirePairsQueryKey,
  shouldLoadMotoTirePairsForRoute,
} from "../../redux/utils/motoTiresQueryKey";
import listingTheme from "../list/listingTheme";

const SORT_OPTIONS = [
  { value: "recommended", label: "מומלץ" },
  { value: "price_asc", label: "מחיר: מהנמוך לגבוה" },
  { value: "price_desc", label: "מחיר: מהגבוה לנמוך" },
];

const TYPE_CHIP_LABELS = {
  set: "סטים מקוריים",
  pair: "זוגות תואמים",
};

const styles = (theme) => ({
  pageRoot: {
    width: "100%",
    maxWidth: 1200,
    boxSizing: "border-box",
    marginTop: 53,
    marginRight: "auto",
    marginBottom: theme.spacing(4),
    marginLeft: "auto",
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    marginTop: theme.spacing(0.5),
  },
  sizeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  sizeChip: {
    backgroundColor: theme.palette.background.paper,
  },
  altActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  quietButton: {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
  },
  mobileToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    position: "sticky",
    top: 60,
    zIndex: 4,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  columns: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "flex-start",
    },
  },
  sidebar: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "block",
      width: 270,
      flex: "0 0 270px",
      position: "sticky",
      top: 72,
      maxHeight: "calc(100vh - 88px)",
      overflowY: "auto",
      marginRight: theme.spacing(3),
    },
  },
  results: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  desktopToolbar: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  },
  mobileCount: {
    display: "block",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  sortControl: {
    minWidth: 0,
    flex: "1 1 150px",
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-input": {
      paddingTop: 10,
      paddingBottom: 10,
    },
    [theme.breakpoints.up("md")]: {
      flex: "0 0 auto",
      minWidth: 220,
    },
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filterChip: {
    backgroundColor: theme.palette.background.paper,
  },
  resultsList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  emptyState: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    textAlign: "center",
  },
  emptyText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  skeletonCard: {
    display: "flex",
    alignItems: "stretch",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
    animation: "$pairPulse 1.6s ease-in-out infinite",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  skeletonMedia: {
    flexShrink: 0,
    width: 256,
    minHeight: 168,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: 140,
    },
  },
  skeletonDetails: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  skeletonTitle: {
    height: 18,
    width: "52%",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
  skeletonRow: {
    height: 12,
    width: "78%",
    borderRadius: 6,
    marginTop: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
  },
  skeletonPrice: {
    flexShrink: 0,
    boxSizing: "border-box",
    width: 220,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      borderLeft: "none",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
  },
  skeletonPriceLine: {
    height: 22,
    width: "70%",
    alignSelf: "center",
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
  },
  skeletonButton: {
    height: 36,
    marginTop: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
  },
  "@keyframes pairPulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.55 },
    "100%": { opacity: 1 },
  },
  "@media (prefers-reduced-motion: reduce)": {
    skeletonCard: {
      animation: "none",
    },
  },
  drawerPaper: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  drawerHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    marginTop: theme.spacing(1),
    marginRight: "auto",
    marginLeft: "auto",
    backgroundColor: theme.palette.divider,
  },
  drawerTitle: {
    paddingTop: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  drawerBody: {
    overflowY: "auto",
    padding: theme.spacing(2),
    flex: "1 1 auto",
  },
  drawerFooter: {
    position: "sticky",
    bottom: 0,
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  "@media (min-width: 1024px)": {
    pageRoot: {
      margin: "130px auto",
    },
    sidebar: {
      top: 146,
      maxHeight: "calc(100vh - 162px)",
    },
  },
});

function formatTireSize(width, profile, rim) {
  return `${width}/${profile}-${rim}`;
}

function dimsFromParams(params) {
  return {
    front: { width: params.fw, profile: params.fp, rim: params.fr },
    rear: { width: params.rw, profile: params.rp, rim: params.rr },
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getSliderValue(filters, facets, priceDraft) {
  if (
    facets.priceMin == null ||
    facets.priceMax == null ||
    facets.priceMin === facets.priceMax
  ) {
    return null;
  }

  if (Array.isArray(priceDraft) && priceDraft.length === 2) {
    return priceDraft;
  }

  const low = filters.minPrice == null ? facets.priceMin : filters.minPrice;
  const high = filters.maxPrice == null ? facets.priceMax : filters.maxPrice;
  return [
    clamp(Math.min(low, high), facets.priceMin, facets.priceMax),
    clamp(Math.max(low, high), facets.priceMin, facets.priceMax),
  ];
}

function brandLabel(key, facets) {
  const match = (facets.brands || []).find((item) => item.brand.toLowerCase() === key);
  return match ? match.brand : key;
}

function priceChipLabel(filters) {
  const { minPrice, maxPrice } = filters;
  if (minPrice != null && maxPrice != null) {
    return `${formatIls(minPrice)} עד ${formatIls(maxPrice)}`;
  }
  if (minPrice != null) {
    return `מ-${formatIls(minPrice)}`;
  }
  return `עד ${formatIls(maxPrice)}`;
}

class TirePairResultsPage extends React.Component {
  state = {
    drawerOpen: false,
    priceDraft: null,
  };

  componentDidMount() {
    const { front, rear } = dimsFromParams(this.props.match.params);

    if (shouldLoadMotoTirePairsForRoute(this.props.catalog, front, rear)) {
      this.props.actions.searchTirePairsByDimensions(front, rear);
    }

    window.scrollTo(0, 0);
  }

  componentDidUpdate(prevProps) {
    const prev = dimsFromParams(prevProps.match.params);
    const next = dimsFromParams(this.props.match.params);
    const prevKey = getMotoTirePairsQueryKey(prev.front, prev.rear);
    const nextKey = getMotoTirePairsQueryKey(next.front, next.rear);
    const prevSearch = (prevProps.location && prevProps.location.search) || "";
    const nextSearch = (this.props.location && this.props.location.search) || "";
    const nextState = {};

    if (prevKey !== nextKey) {
      this.props.actions.searchTirePairsByDimensions(next.front, next.rear);
      window.scrollTo(0, 0);
      if (this.state.drawerOpen) {
        nextState.drawerOpen = false;
      }
      if (this.state.priceDraft) {
        nextState.priceDraft = null;
      }
    } else if (prevSearch !== nextSearch && this.state.priceDraft) {
      nextState.priceDraft = null;
    }

    if (Object.keys(nextState).length > 0) {
      this.setState(nextState);
    }
  }

  searchString() {
    return (this.props.location && this.props.location.search) || "";
  }

  writeFilters = (nextFilters) => {
    const { history, location } = this.props;
    if (!history || !location) {
      return;
    }

    const search = getSearchForPairFilters(location.search || "", nextFilters);
    if (search === (location.search || "")) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search,
      hash: location.hash,
    });
  };

  updateFilters = (patch) => {
    const current = parsePairFiltersFromSearch(this.searchString());
    this.writeFilters({ ...current, ...patch });
  };

  clearFilters = () => {
    const current = parsePairFiltersFromSearch(this.searchString());
    this.writeFilters({
      ...DEFAULT_PAIR_FILTERS,
      brands: [],
      sort: current.sort,
    });
  };

  handleSortChange = (event) => {
    this.updateFilters({ sort: event.target.value });
  };

  handlePriceChange = (value) => {
    this.setState({ priceDraft: Array.isArray(value) ? value.slice() : value });
  };

  handlePriceCommitted = (value) => {
    if (!Array.isArray(value) || value.length < 2) {
      return;
    }

    const facets = getPairFacets(this.props.catalog.motoTirePairs || []);
    let min = Math.round(Number(value[0]));
    let max = Math.round(Number(value[1]));
    if (min > max) {
      const swap = min;
      min = max;
      max = swap;
    }

    this.updateFilters({
      minPrice: facets.priceMin == null || min <= facets.priceMin ? null : min,
      maxPrice: facets.priceMax == null || max >= facets.priceMax ? null : max,
    });
  };

  openDrawer = () => {
    this.setState({ drawerOpen: true });
  };

  closeDrawer = () => {
    this.setState({ drawerOpen: false });
  };

  submitTireRequest = (requestData) => {
    return this.props.actions.submitTireRequest(requestData);
  };

  renderSortControl(filters) {
    const { classes } = this.props;

    return (
      <FormControl variant="outlined" className={classes.sortControl}>
        <Select
          value={filters.sort}
          onChange={this.handleSortChange}
          inputProps={{ "aria-label": "מיון" }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  buildViewModel() {
    const { catalog } = this.props;
    const pairs = catalog.motoTirePairs || [];
    const filters = parsePairFiltersFromSearch(this.searchString());
    const facets = getPairFacets(pairs);

    return {
      filters,
      facets,
      pairs,
      visiblePairs: applyPairFilters(pairs, filters),
      activeCount: countActivePairFilters(filters),
      sliderValue: getSliderValue(filters, facets, this.state.priceDraft),
      sections: getVisiblePairFilterSections(facets),
    };
  }

  renderHeader() {
    const { classes, match } = this.props;
    const { fw, fp, fr, rw, rp, rr } = match.params;
    const frontSize = formatTireSize(fw, fp, fr);
    const rearSize = formatTireSize(rw, rp, rr);

    return (
      <header className={classes.header}>
        <Typography variant="overline" color="textSecondary">
          צמיגים לאופנוע · חיפוש סט
        </Typography>
        <Typography variant="h4" component="h1" className={classes.title}>
          סט צמיגים לאופנוע
        </Typography>
        <div className={classes.sizeRow}>
          <Chip
            variant="outlined"
            className={classes.sizeChip}
            label={
              <React.Fragment>
                {"קדמי "}
                <bdi dir="ltr">{frontSize}</bdi>
              </React.Fragment>
            }
          />
          <Chip
            variant="outlined"
            className={classes.sizeChip}
            label={
              <React.Fragment>
                {"אחורי "}
                <bdi dir="ltr">{rearSize}</bdi>
              </React.Fragment>
            }
          />
          <Button component={Link} to="/" color="primary" size="small">
            שנה מידות
          </Button>
        </div>
        <div className={classes.altActions}>
          <Button
            component={Link}
            to={`/list/moto/${fw}/${fp}/${fr}`}
            variant="outlined"
            size="small"
            className={classes.quietButton}
          >
            חפש רק צמיג קדמי
          </Button>
          <Button
            component={Link}
            to={`/list/moto/${rw}/${rp}/${rr}`}
            variant="outlined"
            size="small"
            className={classes.quietButton}
          >
            חפש רק צמיג אחורי
          </Button>
        </div>
      </header>
    );
  }

  renderChips(filters, facets) {
    const { classes } = this.props;
    const chips = [];

    filters.brands.forEach((brand) => {
      chips.push({
        key: `brand-${brand}`,
        label: brandLabel(brand, facets),
        onDelete: () => this.updateFilters({
          brands: filters.brands.filter((item) => item !== brand),
        }),
      });
    });

    if (filters.localStockOnly) {
      chips.push({
        key: "stock",
        label: "במלאי בארץ",
        onDelete: () => this.updateFilters({ localStockOnly: false }),
      });
    }

    if (filters.type === "set" || filters.type === "pair") {
      chips.push({
        key: "type",
        label: TYPE_CHIP_LABELS[filters.type],
        onDelete: () => this.updateFilters({ type: "all" }),
      });
    }

    if (filters.minPrice != null || filters.maxPrice != null) {
      chips.push({
        key: "price",
        label: priceChipLabel(filters),
        onDelete: () => this.updateFilters({ minPrice: null, maxPrice: null }),
      });
    }

    if (chips.length === 0) {
      return null;
    }

    return (
      <div className={classes.chips}>
        {chips.map((chip) => (
          <Chip
            key={chip.key}
            size="small"
            variant="outlined"
            label={chip.label}
            onDelete={chip.onDelete}
            className={classes.filterChip}
          />
        ))}
        <Button size="small" color="primary" onClick={this.clearFilters}>
          נקה הכל
        </Button>
      </div>
    );
  }

  renderSkeletons() {
    const { classes } = this.props;

    return (
      <div aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <div className={classes.skeletonCard} key={index}>
            <div className={classes.skeletonMedia} />
            <div className={classes.skeletonDetails}>
              <div className={classes.skeletonTitle} />
              <div className={classes.skeletonRow} />
              <div className={classes.skeletonRow} />
            </div>
            <div className={classes.skeletonPrice}>
              <div className={classes.skeletonPriceLine} />
              <div className={classes.skeletonButton} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderResults(model) {
    const { classes, catalog, actions } = this.props;
    const { filters, facets, pairs, visiblePairs, sliderValue, sections } = model;
    const countLabel = visiblePairs.length === pairs.length
      ? `${pairs.length} סטים`
      : `מציג ${visiblePairs.length} מתוך ${pairs.length} סטים`;
    const panelProps = {
      filters,
      facets,
      sliderValue,
      onChange: this.updateFilters,
      onPriceChange: this.handlePriceChange,
      onPriceCommitted: this.handlePriceCommitted,
    };

    return (
      <div className={classes.columns}>
        {sections.any && (
          <aside className={classes.sidebar}>
            <TirePairFiltersPanel {...panelProps} />
          </aside>
        )}
        <div className={classes.results}>
          <div className={classes.desktopToolbar}>
            <Typography variant="body2" color="textSecondary" aria-live="polite">
              {countLabel}
            </Typography>
            {this.renderSortControl(filters)}
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.mobileCount}
            aria-live="polite"
          >
            {countLabel}
          </Typography>
          {this.renderChips(filters, facets)}
          {visiblePairs.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="h6" component="h2">
                אין תוצאות לסינון הזה
              </Typography>
              <Typography variant="body2" color="textSecondary" className={classes.emptyText}>
                נסו להרחיב את החיפוש או לנקות את הסינון
              </Typography>
              <Button variant="outlined" color="primary" onClick={this.clearFilters}>
                נקה סינון
              </Button>
            </div>
          ) : (
            <div className={classes.resultsList}>
              {visiblePairs.map((pair) => (
                <TirePairCard
                  key={getPairKey(pair)}
                  pair={pair}
                  addProductsToCart={actions.addProductsToCart}
                  collectMetric={actions.collectMetric}
                  limitedTimeOffers={catalog.limitedTimeOffers || []}
                  availabilityContext={{
                    currentShipment: catalog.currentShipment,
                    nextShipment: catalog.nextShipment,
                    euContainerMode: catalog.euContainerMode,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { classes, catalog, match } = this.props;
    const { drawerOpen } = this.state;
    const { fw, fp, fr, rw, rp, rr } = match.params;
    const frontSize = formatTireSize(fw, fp, fr);
    const rearSize = formatTireSize(rw, rp, rr);
    const pending = catalog.tirePairsPending;
    const model = this.buildViewModel();
    const { pairs, facets, visiblePairs, activeCount, filters, sliderValue, sections } = model;
    const hasResults = pairs.length > 0;
    const metaTitle = `סט צמיגים לאופנוע: קדמי ${frontSize} + אחורי ${rearSize} - ספורטישופ`;
    const metaDesc = `מגוון סטים של צמיגים לאופנוע, קדמי ${frontSize} ואחורי ${rearSize}, במותגים מובילים - פירלי, מישלין, מצלר, קונטיננטל, ברידגסטון ועוד`;

    return (
      <React.Fragment>
        <Helmet>
          <meta property="og:title" content={metaTitle} />
          <meta property="og:description" content={metaDesc} />
          <meta property="description" content={metaDesc} />
          <title>{metaTitle}</title>
        </Helmet>

        <div className={classes.pageRoot} dir="rtl">
          {this.renderHeader()}

          {pending && this.renderSkeletons()}

          {!pending && hasResults && (
            <div className={classes.mobileToolbar}>
              {sections.any && (
                <Badge
                  color="primary"
                  badgeContent={activeCount}
                  invisible={activeCount === 0}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={this.openDrawer}
                    startIcon={<FilterListIcon />}
                  >
                    סינון
                  </Button>
                </Badge>
              )}
              {this.renderSortControl(filters)}
            </div>
          )}

          {!pending && hasResults && this.renderResults(model)}

          {!pending && !hasResults && (
            <TireRequestCard
              tireSize={`${frontSize} + ${rearSize}`}
              onSubmit={this.submitTireRequest}
            />
          )}
        </div>

        <Drawer
          anchor="bottom"
          open={drawerOpen && !pending && hasResults && sections.any}
          onClose={this.closeDrawer}
          classes={{ paper: classes.drawerPaper }}
        >
          <div className={classes.drawerHandle} />
          <Typography variant="h6" component="h2" className={classes.drawerTitle}>
            סינון
          </Typography>
          <div className={classes.drawerBody}>
            <TirePairFiltersPanel
              variant="plain"
              filters={filters}
              facets={facets}
              sliderValue={sliderValue}
              onChange={this.updateFilters}
              onPriceChange={this.handlePriceChange}
              onPriceCommitted={this.handlePriceCommitted}
            />
          </div>
          <div className={classes.drawerFooter}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={this.closeDrawer}
            >
              {`הצג ${visiblePairs.length} תוצאות`}
            </Button>
          </div>
        </Drawer>
      </React.Fragment>
    );
  }
}

TirePairResultsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
  match: PropTypes.object,
  location: PropTypes.object,
  history: PropTypes.object,
};

const StyledTirePairResultsPage = withStyles(styles)(TirePairResultsPage);

function ThemedTirePairResultsPage(props) {
  return (
    <ThemeProvider theme={listingTheme}>
      <StyledTirePairResultsPage {...props} />
    </ThemeProvider>
  );
}

export default withRouter(ThemedTirePairResultsPage);
